import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, KeyRound, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const emptyCreate = { email: "", full_name: "", password: "", organization_id: "" };

export default function ScoutsPage() {
  const [scouts, setScouts] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreate);

  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ email: "", full_name: "", organization_id: "" });

  const [resetting, setResetting] = useState(null);
  const [newPassword, setNewPassword] = useState("");

  const [toDelete, setToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [scoutsRes, orgsRes] = await Promise.all([
        api.get("/scouts"),
        api.get("/organizations"),
      ]);
      setScouts(scoutsRes.data);
      setOrgs(orgsRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setCreateForm({
      ...emptyCreate,
      organization_id: orgs[0]?.id || "",
    });
    setCreateOpen(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/scouts", {
        email: createForm.email.trim(),
        full_name: createForm.full_name.trim(),
        password: createForm.password,
        organization_id: createForm.organization_id,
      });
      toast.success("Scout created");
      setCreateOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (scout) => {
    setEditing(scout);
    setEditForm({
      email: scout.email,
      full_name: scout.full_name,
      organization_id: scout.organization_id || "",
    });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/scouts/${editing.id}`, {
        email: editForm.email.trim(),
        full_name: editForm.full_name.trim(),
        organization_id: editForm.organization_id,
      });
      toast.success("Scout updated");
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/scouts/${resetting.id}/reset-password`, {
        new_password: newPassword,
      });
      toast.success(`Password reset for ${resetting.email}`);
      setResetting(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/scouts/${toDelete.id}`);
      toast.success("Scout deleted");
      setToDelete(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
      setToDelete(null);
    }
  };

  return (
    <div data-testid="scouts-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Roster</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">
            Scouts
          </h1>
          <p className="text-muted-foreground mt-2 max-w-lg">
            All scouts on the platform and the organization they report to.
          </p>
        </div>
        <Button
          onClick={openCreate}
          disabled={orgs.length === 0}
          className="h-11 rounded-none font-display uppercase tracking-[0.18em]"
          data-testid="create-scout-button"
        >
          <Plus className="h-4 w-4 mr-2" /> New Scout
        </Button>
      </header>

      {orgs.length === 0 && !loading && (
        <div className="rounded-xl border border-border bg-card p-6 mb-6 text-sm text-muted-foreground">
          Create at least one organization before adding scouts.
        </div>
      )}

      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <Table data-testid="scouts-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Full name</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Email</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Organization</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="px-6 py-12 text-center text-muted-foreground">Loading…</TableCell>
              </TableRow>
            ) : scouts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="px-6 py-16 text-center">
                  <Users className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                  <div className="font-display uppercase tracking-wider">No scouts yet</div>
                </TableCell>
              </TableRow>
            ) : (
              scouts.map((s) => (
                <TableRow key={s.id} className="tr-fade border-border hover:bg-muted/30" data-testid={`scout-row-${s.id}`}>
                  <TableCell className="px-6 py-4 font-medium">{s.full_name}</TableCell>
                  <TableCell className="px-6 py-4 text-muted-foreground">{s.email}</TableCell>
                  <TableCell className="px-6 py-4">
                    {s.organization ? (
                      <div className="flex items-center gap-2">
                        <span>{s.organization.name}</span>
                        <Badge variant="secondary" className="rounded-none uppercase tracking-wider text-[10px]">
                          {s.organization.type}
                        </Badge>
                      </div>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-scout-${s.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => { setResetting(s); setNewPassword(""); }} data-testid={`reset-scout-${s.id}`}>
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setToDelete(s)} data-testid={`delete-scout-${s.id}`}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md" data-testid="create-scout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">New Scout</DialogTitle>
            <DialogDescription>The scout will use these credentials to sign in.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="scout-name">Full name</Label>
              <Input id="scout-name" value={createForm.full_name} onChange={(e) => setCreateForm({ ...createForm, full_name: e.target.value })} required data-testid="scout-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scout-email">Email</Label>
              <Input id="scout-email" type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} required data-testid="scout-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="scout-password">Initial password</Label>
              <Input id="scout-password" type="password" minLength={8} value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} required data-testid="scout-password-input" />
              <p className="text-xs text-muted-foreground">At least 8 characters. Scout can change it after first login.</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="scout-org">Organization</Label>
              <Select value={createForm.organization_id} onValueChange={(v) => setCreateForm({ ...createForm, organization_id: v })}>
                <SelectTrigger id="scout-org" data-testid="scout-org-select">
                  <SelectValue placeholder="Select organization" />
                </SelectTrigger>
                <SelectContent>
                  {orgs.map((o) => (
                    <SelectItem key={o.id} value={o.id} data-testid={`scout-org-option-${o.id}`}>
                      {o.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving || !createForm.organization_id} data-testid="scout-create-submit">
                {saving ? "Creating…" : "Create scout"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit */}
      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-scout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Edit Scout</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={handleEdit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Full name</Label>
                <Input id="edit-name" value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} required data-testid="edit-scout-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input id="edit-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} required data-testid="edit-scout-email-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-org">Organization</Label>
                <Select value={editForm.organization_id} onValueChange={(v) => setEditForm({ ...editForm, organization_id: v })}>
                  <SelectTrigger id="edit-org" data-testid="edit-scout-org-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {orgs.map((o) => (
                      <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="edit-scout-submit">
                  {saving ? "Saving…" : "Save changes"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetting} onOpenChange={(open) => !open && setResetting(null)}>
        <DialogContent className="sm:max-w-md" data-testid="reset-scout-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{resetting?.email}</strong>. Share it with them securely.
            </DialogDescription>
          </DialogHeader>
          {resetting && (
            <form onSubmit={handleReset} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-pwd">New password</Label>
                <Input id="new-pwd" type="password" minLength={8} value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required data-testid="reset-scout-password-input" />
                <p className="text-xs text-muted-foreground">At least 8 characters.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setResetting(null)}>Cancel</Button>
                <Button type="submit" disabled={saving} data-testid="reset-scout-submit">
                  {saving ? "Resetting…" : "Reset password"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete */}
      <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-scout-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete scout?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{toDelete?.full_name}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-scout-confirm-button">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
